// HTML void elements legitimately have no closing tag, so an unmatched
// `<br>` / `<img …>` is not "unclosed" — leave them be.
const VOID_HTML_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

/**
 * Escape angle brackets that MDXEditor's markdown→lexical importer cannot parse,
 * which otherwise make it silently render an empty document and blank the whole
 * artifact (BIG-4994). Two cases an LLM emits in email bodies:
 *
 *   1. `<` glued to a non-tag character — `<>` ("Big Basin Labs <> Felipe P."),
 *      `<3` — which is never valid HTML.
 *   2. An opening HTML tag with no matching closing tag — `<small>…` with no
 *      `</small>`, a `<name>` placeholder, etc. MDXEditor blanks on these.
 *
 * Both are rewritten so the `<` renders as a literal `<` (`&lt;`) instead of
 * being treated as markup. A third case, also rewritten:
 *
 *   3. CommonMark autolinks — `<https://…>`, `<mailto:…>`, `<me@example.com>`.
 *      These are valid markdown, but MDXEditor's importer still blanks the
 *      whole editor on them, so the `<` is escaped to render the link as
 *      literal text (e.g. a quoted "On … Name <a@b.com> wrote:" reply header).
 *
 * Things that are genuinely fine are left untouched: `< ` with whitespace
 * (`a < b`), void/self-closing tags (`<br />`, `<img …>`), balanced tags
 * (`<small>…</small>`, the okthx footer, `<a href="https://…">…</a>` — the
 * space after the tag name keeps these from matching the autolink shape), and
 * comments. The transform is idempotent: `&lt;` has no bare `<`, so re-applying
 * it is a no-op.
 *
 * Only the in-app MDXEditor viewer needs this — the outbound markdown→HTML send
 * path (markdown-it) and react-markdown both tolerate all of the above.
 *
 * Heuristic, not a parser: a closed-but-unrecognized tag (`<foo>x</foo>`) or a
 * `<word` with no `>` at all aren't handled, but those don't occur in practice.
 */
export function escapeUnparseableAngleBrackets(markdown: string): string {
  // (1) `<` immediately followed by a non-tag char (`<>`, `<3`).
  let out = markdown.replace(/<(?![A-Za-z/!?\s])/g, "&lt;");

  // (1b) CommonMark autolinks: `<https://…>`, `<mailto:…>`, `<me@example.com>`.
  // MDXEditor's importer blanks the entire editor on these, so escape the `<` to
  // render them as literal text. A real HTML tag (`<a href="https://…">`) does
  // NOT match — the space after the tag name breaks both patterns — so balanced
  // HTML is still left for pass (2). Nor does a link or image destination
  // (`![x](<urn:bb:file:7>)`), which only looks like one: escaping it would turn
  // the image's source into the literal `<urn:…>` and break the picture.
  out = out.replace(/(?<!\]\(\s*)<(?=[A-Za-z][A-Za-z0-9+.-]*:[^<>\s]*>)/g, "&lt;"); // URI autolink
  out = out.replace(/(?<!\]\(\s*)<(?=[^<>\s@]+@[^<>\s@]+>)/g, "&lt;"); // email autolink

  // (2) Opening tags with no matching closing tag. Stack-match per tag name in
  // document order (so a balanced `<small>…</small>` doesn't "close" a separate
  // stray `<small>`); whatever opens are left unmatched get their `<` escaped.
  const tagRe = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)(?:\s[^<>]*)?>/g;
  const unmatchedOpens = new Map<string, number[]>();
  for (const m of out.matchAll(tagRe)) {
    const name = m[2].toLowerCase();
    const offset = m.index ?? 0;
    if (m[1] === "/") {
      unmatchedOpens.get(name)?.pop();
      continue;
    }
    if (m[0].endsWith("/>") || VOID_HTML_ELEMENTS.has(name)) continue;
    const stack = unmatchedOpens.get(name) ?? [];
    stack.push(offset);
    unmatchedOpens.set(name, stack);
  }

  const offsets = [...unmatchedOpens.values()].flat().sort((a, b) => a - b);
  if (offsets.length === 0) return out;

  let result = "";
  let last = 0;
  for (const offset of offsets) {
    // Replace just the `<`; the rest of the tag stays as literal text.
    result += `${out.slice(last, offset)}&lt;`;
    last = offset + 1;
  }
  return result + out.slice(last);
}

/**
 * Escapes unescaped pipe characters within markdown table cells to prevent breaking table syntax.
 *
 * This handles cases where AI-generated content includes pipe characters in table cell content
 * (e.g., "Coffee Project NY | Fort Greene") which would otherwise break the markdown table parsing.
 *
 * @param markdown - The markdown string to process
 * @returns The markdown with pipes escaped within table cells
 */
export function escapePipesInTables(markdown: string): string {
  const lines = markdown.split("\n");
  const processedLines: string[] = [];

  for (const line of lines) {
    // Check if this looks like a table row (starts with optional whitespace then |)
    if (/^\s*\|/.test(line)) {
      processedLines.push(processTableRow(line));
    } else {
      processedLines.push(line);
    }
  }

  return processedLines.join("\n");
}

/**
 * Process a single table row, escaping pipes within cell content while preserving delimiter pipes.
 */
function processTableRow(row: string): string {
  // Extract leading/trailing whitespace
  const leadingWhitespace = row.match(/^\s*/)?.[0] || "";
  const trimmedRow = row.trim();

  // Remove leading and trailing pipes
  let content = trimmedRow.slice(1); // Remove leading |
  if (content.endsWith("|")) {
    content = content.slice(0, -1); // Remove trailing |
  }

  // Split by pipes, but we need to be careful about:
  // 1. Already escaped pipes (\|)
  // 2. Pipes inside link text [text | more](url)
  // 3. Pipes inside link URLs (less common but possible)
  const cells = splitTableCells(content);

  // Escape any unescaped pipes within each cell
  const escapedCells = cells.map((cell) => escapePipesInCell(cell));

  // Reconstruct the row
  return `${leadingWhitespace}| ${escapedCells.join(" | ")} |`;
}

/**
 * Split a table row content into cells, respecting link syntax and already-escaped pipes.
 */
function splitTableCells(content: string): string[] {
  const cells: string[] = [];
  let currentCell = "";
  let inLinkText = false;
  let inLinkUrl = false;
  let i = 0;

  while (i < content.length) {
    const char = content[i];
    const prevChar = i > 0 ? content[i - 1] : "";
    const nextChar = i < content.length - 1 ? content[i + 1] : "";

    // Track if we're inside link text [...]
    if (char === "[" && prevChar !== "\\") {
      inLinkText = true;
      currentCell += char;
      i++;
      continue;
    }

    if (char === "]" && prevChar !== "\\") {
      inLinkText = false;
      currentCell += char;
      // Check if a URL follows
      if (nextChar === "(") {
        inLinkUrl = true;
      }
      i++;
      continue;
    }

    // Track if we're inside link URL (...)
    if (inLinkUrl && char === ")") {
      inLinkUrl = false;
      currentCell += char;
      i++;
      continue;
    }

    // If we hit a pipe that's not escaped and not inside a link, it's a delimiter
    if (char === "|" && prevChar !== "\\" && !inLinkText && !inLinkUrl) {
      cells.push(currentCell.trim());
      currentCell = "";
      i++;
      continue;
    }

    currentCell += char;
    i++;
  }

  // Add the last cell
  if (currentCell) {
    cells.push(currentCell.trim());
  }

  return cells;
}

/**
 * Escape any unescaped pipes within a single cell's content.
 */
function escapePipesInCell(cell: string): string {
  let result = "";

  for (let i = 0; i < cell.length; i++) {
    const char = cell[i];
    const prevChar = i > 0 ? cell[i - 1] : "";

    // Escape unescaped pipes anywhere in the cell
    if (char === "|" && prevChar !== "\\") {
      result += "\\|";
    } else {
      result += char;
    }
  }

  return result;
}
